import { useCallback, useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { db } from '../../shared/firebase';
import { parseOrder } from '../../shared/models';

// Mirrors the iOS DriverEarningsViewModel / DeliveryHistoryViewModel query:
// main `orders` collection where driverId == uid and status is delivered/completed.
// The drivers/{id}/acceptedOrders subcollection is deleted on delivery, so the
// main collection is the authoritative source for history + earnings.
// Status is filtered client-side so casing/legacy variants all count and no
// composite index is required (driverId == X + status in [...] needs one).
export const DELIVERED_STATUSES = ['Delivered', 'delivered', 'Completed', 'completed'];
const isDeliveredStatus = (s) =>
  ['delivered', 'completed'].includes(String(s || '').trim().toLowerCase());

// iOS distance fallback: estimate from the delivery fee when `distance` is
// absent. fee = $15 first mile + $3/extra mi ⇒ miles ≈ (fee − 15)/3 + 1.
const distanceFor = (d, fee) =>
  Number.isFinite(Number(d.distance)) ? Number(d.distance) : Math.max(0, (fee - 15) / 3.0 + 1);

function normalizeDeliveredOrder(id, d) {
  const order = parseOrder(id, d);
  const hasFee = Number.isFinite(Number(d.deliveryFee));
  const deliveryFee = hasFee ? Number(d.deliveryFee) : 0;
  const tipAmount = Number(d.tipAmount) || 0;
  // iOS contract: driverPayout stored at creation excludes the tip; the value
  // written at driver accept already includes it. max() resolves both, and
  // covers orders that predate the payout fields entirely.
  const base = Number.isFinite(Number(d.driverPayout))
    ? Number(d.driverPayout)
    : deliveryFee * 0.9;
  const driverPayout = Math.max(base, deliveryFee * 0.9 + tipAmount);
  const totalEarnings = driverPayout;
  const date = order.updatedAt || order.createdAt || new Date();
  return {
    ...order,
    hasFee,
    deliveryFee,
    tipAmount,
    driverPayout,
    totalEarnings,
    distance: distanceFor(d, deliveryFee),
    driverCommission: Number.isFinite(Number(d.driverCommission))
      ? Number(d.driverCommission)
      : deliveryFee * 0.9,
    platformCommission: Number.isFinite(Number(d.platformCommission))
      ? Number(d.platformCommission)
      : deliveryFee * 0.1,
    date, // earning/sort date = updatedAt ?? createdAt (matches iOS)
  };
}

export async function fetchDeliveredOrders(driverId) {
  // Single-equality query only — no composite index needed. Delivered/completed
  // filtering happens client-side to tolerate any status casing.
  const q = query(
    collection(db, 'orders'),
    where('driverId', '==', driverId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .filter(doc => isDeliveredStatus(doc.data().status))
    .map(doc => normalizeDeliveredOrder(doc.id, doc.data()))
    .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));
}

// iOS recomputes drivers/{id}.earnings + totalDeliveries from completed work
// on every delivery. Orders finished before the payout fields existed never
// incremented the doc, so reconcile it from the authoritative orders
// collection whenever earnings load (write only moves totals up).
async function reconcileDriverTotals(driverId, orders) {
  try {
    const ref = doc(db, 'drivers', driverId);
    const snap = await getDoc(ref);
    const d = snap.exists() ? snap.data() : {};
    const computed = orders.reduce((s, o) => s + o.totalEarnings, 0);
    const computedCount = orders.length;
    const current = Math.max(Number(d.earnings) || 0, Number(d.totalEarnings) || 0);
    const currentCount = Number(d.totalDeliveries) || 0;
    if (computed > current || computedCount > currentCount) {
      await setDoc(ref, {
        earnings: Math.max(computed, current),
        totalEarnings: Math.max(computed, current),
        totalDeliveries: Math.max(computedCount, currentCount),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }
  } catch (e) {
    console.warn('reconcileDriverTotals', e);
  }
}

export function useDeliveredOrders(driverId) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!driverId) {
      setOrders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const fetched = await fetchDeliveredOrders(driverId);
      setOrders(fetched);
      reconcileDriverTotals(driverId, fetched); // fire-and-forget self-heal
      setError(null);
    } catch (e) {
      console.error('fetchDeliveredOrders', e);
      setError(e);
    }
    setLoading(false);
  }, [driverId]);

  // Live: earnings and the chart update the moment a delivery is completed.
  useEffect(() => {
    if (!driverId) {
      setOrders([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    let first = true;
    const q = query(collection(db, 'orders'), where('driverId', '==', driverId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .filter(d => isDeliveredStatus(d.data().status))
          .map(d => normalizeDeliveredOrder(d.id, d.data()))
          .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));
        setOrders(list);
        if (first) {
          first = false;
          reconcileDriverTotals(driverId, list); // fire-and-forget self-heal
        }
        setError(null);
        setLoading(false);
      },
      (e) => {
        console.error('useDeliveredOrders', e);
        setError(e);
        setLoading(false);
      },
    );
    return unsub;
  }, [driverId]);

  return { orders, loading, error, reload };
}

// iOS DriverEarningsViewModel.dateRange:
//   Day = start of today → now, Week = now−7d → now,
//   Month = start of month → now, Year = start of year → now.
export function earningsRange(filter) {
  const now = new Date();
  switch (filter) {
    case 'Day': {
      const s = new Date(now);
      s.setHours(0, 0, 0, 0);
      return [s, now];
    }
    case 'Week':
      return [new Date(now.getTime() - 7 * 864e5), now];
    case 'Month':
      return [new Date(now.getFullYear(), now.getMonth(), 1), now];
    case 'Year':
      return [new Date(now.getFullYear(), 0, 1), now];
    default:
      return [new Date(0), now];
  }
}

// iOS DeliveryHistoryTimeFilter.contains: rolling period ending now
// (Day = last 24h, Week = last 7d, Month = last month, Year = last year).
export function historyRangeStart(filter) {
  const d = new Date();
  switch (filter) {
    case 'Day': d.setDate(d.getDate() - 1); break;
    case 'Month': d.setMonth(d.getMonth() - 1); break;
    case 'Year': d.setFullYear(d.getFullYear() - 1); break;
    case 'Week':
    default: d.setDate(d.getDate() - 7); break;
  }
  return d;
}

// iOS shows "Order #<orderId prefix 8>" — orderId is the document id.
// Prefer the friendlier orderNumber when it differs from the doc id.
export function displayOrderNumber(order) {
  const num = order.orderNumber || '';
  return num && num !== order.id ? num : (order.id || '').slice(0, 8);
}
